import { Alert, TextField } from '@mui/material'
import { type AxiosError } from 'axios'
import React from 'react'

import { isPreFlightEntityNotFoundError } from 'queries/status-checks/pre-flight/utils'

import type { DynatracePreFlightErrorResponse } from 'queries/status-checks/pre-flight/types'

type TestDynatracePreFlightCheckResultNotificationProps =
  | {
      error: AxiosError<DynatracePreFlightErrorResponse> | null | undefined
      isSuccess?: never
    }
  | {
      error?: never
      isSuccess: boolean
    }

/**
 * Displays feedback notifications based on the result of a Dynatrace pre-flight check.
 * Shows success, warning, or error messages depending on the check outcome. Does not
 * render an output if no `error` prop is included and the `isSuccess` flag is false.
 */
export const TestDynatracePreFlightCheckResultNotification: React.FC<
  TestDynatracePreFlightCheckResultNotificationProps
> = ({ error, isSuccess }) => {
  if (isSuccess) {
    return <Alert severity="success">Dynatrace match successfully found.</Alert>
  }

  if (!error) return null

  const isNotFoundError = isPreFlightEntityNotFoundError(error)

  if (isNotFoundError) {
    return (
      <Alert severity="warning">
        No Dynatrace match found for this Kubernetes object yet. This Health
        Check can still be created.
      </Alert>
    )
  }

  return (
    <>
      <Alert severity="error">
        We encountered an error when querying Dynatrace for a matching entity.
        See details below to help diagnose the issue.
      </Alert>
      <TextField
        disabled
        fullWidth
        data-testid="test-status-check-integration-response-code"
        label="Response Code"
        value={error.status}
      />
      <TextField
        disabled
        fullWidth
        multiline
        data-testid="test-status-check-integration-response-body"
        label="Response Body"
        rows={4}
        value={
          error.response?.data.errorMessage ??
          error.response?.data ??
          error.message
        }
      />
    </>
  )
}
